import type { FilterGroup, FilterSelection, SearchToken } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { DatasetFilterController } from "./DatasetFilterController";
import { FilterResultChip } from "./FilterResultChip";
import styles from "./AppliedFilterChips.module.css";

export type AppliedFilterChipsProps = {
  controller: DatasetFilterController;
  // 칩으로 그릴 facet 그룹이다. 어느 그룹을 보일지는 호출자가 고른다. 레일은 레일 밖 조건만,
  // 시트 프리뷰는 선택이 있는 그룹 전부를 넘긴다.
  groups: readonly FilterGroup[];
  selection: FilterSelection;
  labelTokens: readonly SearchToken[];
  // 커밋된 이름 검색어다. 있으면 "검색: …" 칩으로 표시한다. 학습 세션 필터 바와 같은 규약이다.
  searchQuery?: string;
  disabled: boolean;
  // 칩 묶음의 접근성 이름이다.
  label: string;
  className?: string;
};

// 적용 중인 조건의 ✕ 칩 묶음이다. 값 표기(값→라벨 매핑)와 "그 조건만 해제" 연결을 이 컴포넌트가
// 소유한다. 칩 한 개의 모양은 FilterResultChip 이 소유한다. 필터 레일(적용 조건 행)과 고급 필터
// 시트(현재 필터 프리뷰)가 같은 칩을 함께 쓴다.
export function AppliedFilterChips({
  controller,
  groups,
  selection,
  labelTokens,
  searchQuery = "",
  disabled,
  label,
  className,
}: AppliedFilterChipsProps): ReactNode {
  return (
    <div
      className={[styles.chips, className ?? ""].filter(Boolean).join(" ")}
      role="group"
      aria-label={label}
    >
      {groups.map((group) => (
        <FilterResultChip
          key={group.value}
          label={`${group.label}: ${appliedValueLabels(group, selection)}`}
          removeLabel={`${group.label} 필터 해제`}
          disabled={disabled}
          onRemove={() => controller.filterController.setGroupSelection(group.value, [])}
        />
      ))}
      {labelTokens.map((token) => (
        <FilterResultChip
          key={token.value}
          label={token.label}
          removeLabel={`라벨 조건 ${token.label} 해제`}
          disabled={disabled}
          onRemove={() => controller.removeLabelToken(token.value)}
        />
      ))}
      {searchQuery !== "" ? (
        <FilterResultChip
          label={`검색: ${searchQuery}`}
          removeLabel={`검색 ${searchQuery} 해제`}
          disabled={disabled}
          onRemove={() => controller.clearSearch()}
        />
      ) : null}
    </div>
  );
}

// 선택 값들을 표시 라벨로 요약한다. 값→라벨 매핑은 그룹의 filters 가 소유한다.
function appliedValueLabels(group: FilterGroup, selection: FilterSelection): string {
  const values = selection[group.value] ?? [];
  return values
    .map((value) => group.filters.find((filter) => filter.value === value)?.label ?? value)
    .join(", ");
}
